import { MortgageLeadTasso } from '@tassomutuo-backoffice/types';
import { getLeadTassoDuplicateOutcome } from './leadTassoErrors';

/** "Wait briefly" between bounded retries of an unresolved `409`. */
export const LEAD_TASSO_DUPLICATE_RETRY_DELAY_MS = 300;

/** The original attempt plus two bounded retries before surfacing a recoverable error. */
export const LEAD_TASSO_DUPLICATE_MAX_ATTEMPTS = 3;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * A `409 Duplicate request` with no `existingRequestId` means the write this
 * `Idempotency-Key` started is still being processed, not that it failed.
 * Retrying the same key a bounded number of times, with a short pause, lets
 * it resolve into the same outcome as a fresh `201` without minting a second
 * key. `null` return means a `409` resolved the acquisition without handing
 * back the created lead's body — the page does not need those fields either
 * way.
 */
export const submitMortgageLeadTassoWithRecovery = async (
  submit: () => Promise<MortgageLeadTasso>
): Promise<MortgageLeadTasso | null> => {
  for (let attempt = 1; attempt <= LEAD_TASSO_DUPLICATE_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await submit();
    } catch (error) {
      const outcome = getLeadTassoDuplicateOutcome(error);
      if (outcome === 'resolved') return null;

      const canRetry = outcome === 'pending' && attempt < LEAD_TASSO_DUPLICATE_MAX_ATTEMPTS;
      if (!canRetry) throw error;

      await wait(LEAD_TASSO_DUPLICATE_RETRY_DELAY_MS);
    }
  }

  // Unreachable: the loop above always returns or throws.
  throw new Error('submitMortgageLeadTassoWithRecovery: retry loop exhausted');
};
